import { api } from "@/api";
import { useAppSelector } from "@/hooks/useAppSelector";
import { getRussianMonthFromEnglish } from "@/utils";
import { useEffect, useState, useRef } from "react";
import { Group } from "@visx/group";
import { Bar } from "@visx/shape";
import { scaleBand, scaleLinear } from "@visx/scale";
import { AxisLeft, AxisBottom } from "@visx/axis";
import { Tooltip, defaultStyles } from "@visx/tooltip";
import { useTooltip } from "@visx/tooltip";
import useDarkMode from "use-dark-mode";

interface BarDataItem {
  name: string;
  [key: string]: number | string;
}

interface TooltipData {
  name: string;
  requisite: string;
  plan: number;
  fact: number;
  factColor: string;
  planColor: string;
}

function getBarColor(barIndex: number, type: "solid" | "dull") {
  const fullColors = [
    "#3B82F6", // Blue
    "#10B981", // Emerald
    "#F59E0B", // Amber
    "#EF4444", // Red
    "#8B5CF6", // Purple
    "#06B6D4", // Cyan
  ];
  const dullColors = [
    "#93C5FD", // Light blue
    "#6EE7B7", // Light emerald
    "#FCD34D", // Light amber
    "#FCA5A5", // Light red
    "#C4B5FD", // Light purple
    "#67E8F9", // Light cyan
  ];

  const colorIndex = barIndex % fullColors.length;

  if (type === "solid") {
    return fullColors[colorIndex];
  } else {
    return dullColors[colorIndex];
  }
}

export function SummaryChart({ month, year }: { month: string; year: string }) {
  const locations = useAppSelector((state) => state.handbooks.location);
  const rents = useAppSelector((state) => state.handbooks.agreements);
  const requisites = useAppSelector((state) => state.handbooks.requisites);

  const [barData, setBarData] = useState<BarDataItem[]>([]);
  const [uniqueRequisites, setUniqueRequisites] = useState<string[]>([]);
  const [containerWidth, setContainerWidth] = useState(800);
  const containerRef = useRef<HTMLDivElement>(null);

  const isDarkMode = useDarkMode();

  const { tooltipData, tooltipLeft, tooltipTop, showTooltip, hideTooltip } =
    useTooltip<TooltipData>();

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const locationsBrief = await Promise.all(
        locations.map(async (location) => {
          const brief = {
            name: location.display_name,
            requisites: [] as { name: string; fact: number; plan: number }[],
          };

          try {
            const data = await api.analytics.summary(
              getRussianMonthFromEnglish(month),
              year,
              location.id,
            );

            data.data.rent_plan.forEach((rent) => {
              const actualContract = rents.find((r) => r.id === rent.rent_id);
              const actialRequisite = requisites.find(
                (r) => r.id === actualContract?.requisiteId,
              );
              if (!actualContract || !actialRequisite) return;

              if (
                !brief.requisites.find(
                  (r) => r.name === actialRequisite.displayName,
                )
              ) {
                brief.requisites.push({
                  name: actialRequisite.displayName,
                  fact: 0,
                  plan: 0,
                });
              }

              const briefIndex = brief.requisites.findIndex(
                (r) => r.name === actialRequisite.displayName,
              );
              brief.requisites[briefIndex].plan += rent.price;

              const payment = data.data.actual_payments.find(
                (p) => p.office_id === rent.office_id,
              );
              if (payment) {
                brief.requisites[briefIndex].fact += payment.total_paid || 0;
              }
            });
          } catch (err) {
            console.error(err);
          }

          brief.requisites.forEach((_req, index) => {
            brief.requisites[index].plan -= brief.requisites[index].fact;
            if (brief.requisites[index].plan < 0)
              brief.requisites[index].plan = 0;
          });
          return brief;
        }),
      );

      const newUniqueRequisites: string[] = [];

      const newBarData = locationsBrief
        .map((location) => {
          location.requisites.forEach((req) => {
            if (!newUniqueRequisites.includes(req.name)) {
              newUniqueRequisites.push(req.name);
            }
          });

          const bar: BarDataItem = {
            name: location.name,
          };

          location.requisites.forEach((req) => {
            bar[`${newUniqueRequisites.indexOf(req.name)}-plan`] =
              req.plan || 0;
            bar[`${newUniqueRequisites.indexOf(req.name)}-fact`] =
              req.fact || 0;
          });

          return bar;
        })
        .filter((el) => el !== null);

      setBarData(newBarData);
      setUniqueRequisites(newUniqueRequisites);
    };

    fetchData();
  }, [locations, year, month, rents, requisites]);

  const height = 280;
  const margin = { top: 30, right: 40, bottom: 60, left: 80 };

  const xMax = containerWidth - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  const groupedData = barData.map((d: BarDataItem) => {
    const group: any = { name: d.name };
    uniqueRequisites.forEach((req, reqIndex) => {
      group[`${req}-fact`] = (d[`${reqIndex}-fact`] as number) || 0;
      group[`${req}-plan`] = (d[`${reqIndex}-plan`] as number) || 0;
    });
    return group;
  });

  const xScale = scaleBand({
    range: [0, xMax],
    round: true,
    domain: barData.map((d: BarDataItem) => d.name),
    padding: 0.4,
  });

  const yScale = scaleLinear<number>({
    range: [yMax, 0],
    round: true,
    domain: [
      0,
      Math.max(
        ...groupedData.flatMap((d: any) =>
          uniqueRequisites.map((req) => {
            const fact = d[`${req}-fact`] || 0;
            const plan = d[`${req}-plan`] || 0;
            return fact + plan;
          }),
        ),
      ) || 1,
    ],
  });

  const formatMoney = (value: number) => {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: "RUB",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const tooltipStyles = {
    ...defaultStyles,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    color: "white",
    borderRadius: "8px",
    border: "none",
    zIndex: 9999,
    padding: "8px 12px",
    fontSize: "13px",
  };

  return (
    <div
      ref={containerRef}
      className="bg-white dark:bg-zinc-900 p-4 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 w-full"
      style={{ height: height + 20 }}
    >
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${containerWidth} ${height}`}
      >
        <Group left={margin.left} top={margin.top}>
          {groupedData.map((d: any, _i: number) => {
            const barWidth = xScale.bandwidth();
            const barX = xScale(d.name) || 0;
            const groupWidth = barWidth / uniqueRequisites.length;

            return uniqueRequisites.map((req, reqIndex) => {
              const factValue = d[`${req}-fact`] || 0;
              const planValue = d[`${req}-plan`] || 0;

              const groupX = barX + reqIndex * groupWidth;

              const factHeight = Math.abs(yScale(factValue) - yScale(0));
              const planHeight = Math.abs(yScale(planValue) - yScale(0));

              const factY = yMax - factHeight;
              const planY = factY - planHeight;

              return (
                <Group key={`${d.name}-${reqIndex}`}>
                  {factValue > 0 && (
                    <Bar
                      x={groupX}
                      y={factY}
                      width={groupWidth * 0.8}
                      height={factHeight}
                      fill={getBarColor(reqIndex, "solid")}
                      rx={4}
                      ry={4}
                      onMouseMove={(event) => {
                        const coords = { x: event.clientX, y: event.clientY };
                        showTooltip({
                          tooltipLeft: coords.x,
                          tooltipTop: coords.y,
                          tooltipData: {
                            name: d.name,
                            requisite: req,
                            plan: planValue,
                            fact: factValue,
                            factColor: getBarColor(reqIndex, "solid"),
                            planColor: getBarColor(reqIndex, "dull"),
                          },
                        });
                      }}
                      onMouseLeave={hideTooltip}
                    />
                  )}
                  {planValue > 0 && (
                    <Bar
                      x={groupX}
                      y={planY}
                      width={groupWidth * 0.8}
                      height={planHeight}
                      fill={getBarColor(reqIndex, "dull")}
                      rx={4}
                      ry={4}
                      onMouseMove={(event) => {
                        const coords = { x: event.clientX, y: event.clientY };
                        showTooltip({
                          tooltipLeft: coords.x,
                          tooltipTop: coords.y,
                          tooltipData: {
                            name: d.name,
                            requisite: req,
                            plan: planValue,
                            fact: factValue,
                            factColor: getBarColor(reqIndex, "solid"),
                            planColor: getBarColor(reqIndex, "dull"),
                          },
                        });
                      }}
                      onMouseLeave={hideTooltip}
                    />
                  )}
                </Group>
              );
            });
          })}

          <AxisBottom
            top={yMax}
            scale={xScale}
            tickLabelProps={() => ({
              fill: isDarkMode.value ? "#E5E7EB" : "#374151",
              fontSize: 14,
              textAnchor: "middle",
            })}
          />

          <AxisLeft
            scale={yScale}
            tickLabelProps={() => ({
              fill: isDarkMode.value ? "#E5E7EB" : "#374151",
              fontSize: 14,
              textAnchor: "end",
            })}
          />
        </Group>
      </svg>

      {tooltipData && (
        <Tooltip
          left={(tooltipLeft ?? 0) + 12}
          top={(tooltipTop ?? 0) + 12}
          style={tooltipStyles}
        >
          <div className="min-w-52">
            <div className="font-semibold">{tooltipData.name}</div>
            <div className="mb-1 text-white/70">{tooltipData.requisite}</div>
            <div className="grid grid-cols-[auto_1fr_auto] items-center gap-x-2 gap-y-1">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: tooltipData.planColor }}
              />
              <span className="text-white/70">План всего</span>
              <strong>
                {formatMoney(tooltipData.plan + tooltipData.fact)}
              </strong>

              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: tooltipData.factColor }}
              />
              <span className="text-white/70">Оплачено</span>
              <strong>{formatMoney(tooltipData.fact)}</strong>

              <span />
              <span className="text-white/70">Осталось</span>
              <strong>{formatMoney(tooltipData.plan)}</strong>

              <span />
              <span className="text-white/70">Оплата</span>
              <span>
                {tooltipData.plan + tooltipData.fact > 0
                  ? `${Math.round((tooltipData.fact / (tooltipData.plan + tooltipData.fact)) * 100)}%`
                  : "—"}
              </span>
            </div>
          </div>
        </Tooltip>
      )}
    </div>
  );
}
